/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable , */
import React, {
  useEffect,
  useRef,
  useState,
  FunctionComponent,
  useMemo,
} from "react";
import { AsyncTypeahead, TypeaheadResult } from "react-bootstrap-typeahead";
import { useTranslation } from "react-i18next";
import { Overlay, Tooltip } from "react-bootstrap";
import DicomDictionaryService from "../../services/storage/dicom_dictionary_service";
import { DictionaryData } from "../../types/dicom";
import {
  BETWEEN_DATE_INTERVAL,
  BETWEEN_TIME_INTERVAL,
  DATE_INTERVAL,
  TIME_INTERVAL,
  DATE,
  DATE_RANGE,
  TIME,
  TIME_RANGE,
} from "../../constant";
import { BigText } from "../Displays/BigText";
import { DicomInputProps } from "../../types/components/input";

const DicomInput: FunctionComponent<DicomInputProps> = ({
  onChange,
  disabled,
  initialValue = "",
  placeholder,
  isSearchByVr = false,
  operator,
  isDark = false,
}: DicomInputProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [dicomOptions, setDicomOptions] = useState<DictionaryData[]>([]);
  const [tagName, setTagName] = useState("");
  const [hideResults, setHideResults] = useState(true);

  const { t } = useTranslation("rule");
  const divRef = React.useRef(null);
  const ref = useRef(null);
  const [targetInput, setTargetInput] = useState(null);
  const [show, setShow] = useState(false);

  const getVr = useMemo(() => {
    switch (operator) {
      case DATE_INTERVAL:
      case BETWEEN_DATE_INTERVAL:
      case DATE:
      case DATE_RANGE:
        return "DA"; // where vr
      case TIME:
      case TIME_RANGE:
      case BETWEEN_TIME_INTERVAL:
      case TIME_INTERVAL:
        return "TM";
      default:
        return "DT";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operator]);

  useEffect(() => {
    async function getNameByTag(tag: string | undefined) {
      try {
        if (initialValue) {
          const allPromise: any[] = [];
          const str: string[] = [];

          initialValue.split(".").forEach((comp) => {
            const tagV = comp.substring(
              comp.indexOf("("),
              comp.indexOf(")") + 1
            );

            if (tagV) {
              allPromise.push(DicomDictionaryService.getNameByTag(tagV));
              str.push(comp.substring(comp.indexOf(")") + 1, comp.length));
            }
          });

          const rs = await Promise.all(allPromise);
          let tnStr = "";
          rs.forEach((r, index) => {
            if (r.length > 0) tnStr += `${r[0].name}${str[index]}.`;
          });
          setTagName(tnStr.substring(0, tnStr.length - 1));
        } else setTagName("");
      } catch (e) {
        console.log(e);
      }
    }
    getNameByTag(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (tagName && tagName !== "") {
      setTargetInput(divRef.current);
    }
  }, [tagName]);

  const getNewOptions = (search: string) => {
    try {
      setHideResults(true);
      if (onChange) {
        onChange(search.length < 5 ? "" : search);
      }

      if (search.length < 3 || /.?\(.*\)(\[([\d]*)?\])?$/i.test(search)) {
        setDicomOptions([]);
        setIsLoading(false);
        return;
      }

      const searchParts = search.split(".");
      const searchText = searchParts.slice(-1)[0];
      if (searchText[0] === "(") {
        return;
      }
      setHideResults(false);
      setIsLoading(true);
      let promise;
      if (isSearchByVr) {
        promise = DicomDictionaryService.searchTags(searchText, getVr);
      } else {
        promise = DicomDictionaryService.searchTags(searchText);
      }
      promise
        .then((results) => {
          const cloneDicomOptions = results.map((item) => {
            return {
              ...item,
              tag: `${searchParts.slice(0, -1).join(".")}${
                searchParts.length > 1 ? "." : ""
              }${item.tag}`,
            };
          });
          setDicomOptions(cloneDicomOptions);
        })
        .catch(() => {
          if (onChange) {
            onChange(search.length < 5 ? "" : search);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  const handleOnMouseOver = (event: any) => {
    setShow(true);
    setTargetInput(divRef.current);
  };

  const handleOnMouseLeave = (event: any) => {
    setShow(false);
    setTargetInput(divRef.current);
  };

  const maxWidthMenuOption = useMemo(() => {
    return divRef?.current?.clientWidth - 30; // wrapper width - padding left and right.
  }, [divRef?.current]);

  return (
    <div ref={ref}>
      <Overlay
        target={targetInput}
        placement="top"
        container={ref.current}
        containerPadding={20}
        show={show && tagName !== ""}
      >
        <Tooltip>{tagName}</Tooltip>
      </Overlay>
      {/* eslint-disable-next-line  */}
      <div ref={divRef}>
        <AsyncTypeahead
          inputProps={{
            onMouseOver: handleOnMouseOver,
            onMouseLeave: handleOnMouseLeave,
          }}
          open={!hideResults}
          useCache={false}
          delay={0}
          positionFixed
          disabled={disabled}
          filterBy={() => true}
          maxResults={20}
          id="async-example"
          isLoading={isLoading}
          labelKey="tag"
          defaultInputValue={initialValue}
          className={`${
            isDark ? "typeahead-dark" : "typeahead-light"
          } dicom-typeahead`}
          onSearch={getNewOptions}
          paginationText={`${t("display_more")}...`}
          options={dicomOptions}
          placeholder={`${placeholder || t("tag")}`}
          renderMenuItemChildren={(option: TypeaheadResult<any>) => (
            <div className="child" role="button">
              <BigText text={option.name} maxWidth={maxWidthMenuOption} />
            </div>
          )}
          onChange={(value) => {
            setHideResults(true);
            const [result] = value;
            let tag = "";
            if (result) {
              // @ts-ignore
              tag = result?.tag;
            }
            if (onChange && tag.length > 2) {
              onChange(tag);
            }
          }}
        />
      </div>
    </div>
  );
};

export default DicomInput;
